import React, { useState, lazy, Suspense } from 'react';
import './home.css';
import Papa from 'papaparse';
import { FaMapMarkerAlt, FaSyncAlt, FaRocket, FaDatabase, FaChartLine } from 'react-icons/fa';


const GLBViewer = lazy(() => import('../components/GLBViewer'));


const Home: React.FC = () => {
  const [inputLat, setInputLat] = useState('');
  const [inputLng, setInputLng] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalPoints, setOriginalPoints] = useState<{ lat: number; lng: number }[]>([]);
  const [rotatedPoints, setRotatedPoints] = useState<{ lat: number; lng: number; p_lat: number; p_lng: number; period: string }[]>([]);
  const [modelUrl, setModelUrl] = useState('PaleoAtlas.glb');


  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not available in this browser.');
      return;
    }

    setLoading(true);
    setError(null);
    setOriginalPoints([]);
    setRotatedPoints([]);
    setModelUrl('PaleoAtlas.glb');

    navigator.geolocation.getCurrentPosition(
      async pos => {
        const lat = pos.coords.latitude.toFixed(6);
        const lng = pos.coords.longitude.toFixed(6);

        setInputLat(lat);
        setInputLng(lng);

        // ✅ Muestra el punto actual en el globo presente inmediatamente
        setOriginalPoints([{ lat: parseFloat(lat), lng: parseFloat(lng) }]);

        console.log('📍 Coordinates:', { lat, lng });
        console.log('🌐 API URL:', `${import.meta.env.BASE_URL}api/rotate`);

        try {
          const res = await fetch(`${import.meta.env.BASE_URL}api/rotate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              lat: parseFloat(lat),
              lng: parseFloat(lng),
            }),
          });

          console.log('📡 Status:', res.status);

          if (!res.ok) {
            const resClone = res.clone();
            let errorMessage = `Error ${res.status}`;
            try {
              const errorData = await res.json();
              errorMessage = errorData.message || errorData.error || errorMessage;
            } catch {
              const text = await resClone.text().catch(() => '');
              const isHtml = text.trimStart().startsWith('<!');
              if (!isHtml && text) {
                errorMessage = text;
              } else if (res.status === 404) {
                errorMessage = 'API endpoint not found. Check backend configuration.';
              } else {
                errorMessage = `Server error ${res.status}. Backend not responding.`;
              }
            }
            throw new Error(errorMessage);
          }

          const csvText = await res.text();
          console.log('✅ Response: CSV received');

          Papa.parse(csvText, {
            header: true,
            complete: results => {
              console.log('📊 Headers:', results.meta.fields);
              console.log('📊 Raw first row:', results.data[0]);

              const pts = (results.data as any[])
                .map(v => ({
                  lat:    parseFloat(v.p_lat),
                  lng:    parseFloat(v.p_lng),
                  p_lat:  parseFloat(v.p_lat),
                  p_lng:  parseFloat(v.p_lng),
                  period: 'Jurassic-Cretaceous',
                }))
                .filter(v => !isNaN(v.lat) && !isNaN(v.lng));

              console.log('📍 Parsed pts:', pts);

              // ✅ No limpiamos originalPoints — GLBViewer prioriza rotatedPoints automáticamente
              setRotatedPoints(pts);
              setModelUrl('Jurassic-Cretaceous.glb');
            },
            error: () => setError('Error reading rotated CSV.'),
          });

        } catch (err: any) {
          console.error('💥 Error:', err);
          setError(err.message || 'Error calculating paleocoordinates');
        } finally {
          setLoading(false);
        }
      },
      geoErr => {
        console.error('🚫 Geolocation:', geoErr);

        if (geoErr.code === geoErr.PERMISSION_DENIED) {
          setError('You need to allow location access in your browser.');
        } else if (geoErr.code === geoErr.POSITION_UNAVAILABLE) {
          setError('Could not determine your location. Please try again.');
        } else if (geoErr.code === geoErr.TIMEOUT) {
          setError('Location request timed out. Please try again.');
        } else {
          setError('Could not get your location.');
        }
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };


  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Discover Earth's
              <span className="gradient-text"> Ancient Geography</span>
            </h1>
            <p className="hero-description">
              Transform modern coordinates into their ancient positions using cutting-edge
              plate tectonic models. Visualize where any location was 145 million years ago.
            </p>

            <div className="hero-actions">
              <button
                onClick={handleUseCurrentLocation}
                disabled={loading}
                className="primary-button"
              >
                {loading ? (
                  <>
                    <FaSyncAlt className="spinning" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <FaMapMarkerAlt />
                    Try My Location
                  </>
                )}
              </button>

              <a href="/paca/rotate" className="secondary-button">
                <FaRocket />
                Get Started
              </a>
            </div>

            {error && (
              <div className="hero-error">
                <strong>Error:</strong> {error}
              </div>
            )}

            {inputLat && inputLng && !error && !loading && (
              <div className="hero-success">
                <strong>Current location:</strong> {inputLat}, {inputLng}
                {rotatedPoints.length > 0 && <span> → Ancient position on globe</span>}
              </div>
            )}

            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">5</div>
                <div className="stat-label">Plate Models</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">1100</div>
                <div className="stat-label">Million Years</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">3D</div>
                <div className="stat-label">Visualization</div>
              </div>
            </div>
          </div>

          <div className="hero-globe">
            <Suspense fallback={
              <div className="globe-loading">
                <div className="loading-spinner"></div>
                <p>Loading Earth Model...</p>
              </div>
            }>
              <GLBViewer
                modelUrl={modelUrl}
                originalPoints={originalPoints}
                rotatedPoints={rotatedPoints.length > 0 ? rotatedPoints : null}
                selectedPeriod={rotatedPoints.length > 0 ? 'Jurassic-Cretaceous' : null}
                selectedPeriodLabel={rotatedPoints.length > 0 ? 'Jurassic-Cretaceous' : null}
                exportEnabled={false}
              />
            </Suspense>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Why Choose PACA?</h2>
          <p className="section-subtitle">
            A powerful, accessible tool for paleogeographic research and education
          </p>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon blue">
                <FaDatabase />
              </div>
              <h3>Scientific Accuracy</h3>
              <p>
                Powered by five leading Global Plate Models including PALEOMAP,
                MERDITH2021, and others, providing research-grade reconstructions.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon teal">
                <FaChartLine />
              </div>
              <h3>Interactive Visualization</h3>
              <p>
                Explore ancient Earth with our 3D globe interface. Export high-quality
                visualizations and coordinate data for further analysis.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon indigo">
                <FaRocket />
              </div>
              <h3>Easy to Use</h3>
              <p>
                Upload your coordinates or use your current location. Get instant
                paleogeographic reconstructions with just a few clicks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-section">
        <div className="container">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">
            Four simple steps to discover ancient Earth
          </p>

          <div className="steps-timeline">
            <div className="step-item">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3>Input Data</h3>
                <p>Upload coordinates or use your current location</p>
              </div>
            </div>

            <div className="step-connector"></div>

            <div className="step-item">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3>Select Model</h3>
                <p>Choose from 0 to 1100 million years ago models</p>
              </div>
            </div>

            <div className="step-connector"></div>

            <div className="step-item">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3>Calculate</h3>
                <p>PACA applies plate tectonic models</p>
              </div>
            </div>

            <div className="step-connector"></div>

            <div className="step-item">
              <div className="step-number">04</div>
              <div className="step-content">
                <h3>Visualize & Export</h3>
                <p>View on 3D globe and download results</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Explore Ancient Earth?</h2>
            <p>
              Start reconstructing paleocoordinates with our powerful web-based tool.
              Perfect for research, education, and exploration.
            </p>
            <a href="/paca/rotate" className="cta-button">
              Launch PACA Tool
              <FaRocket />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;